import type { RefreshFailureReason } from '@/lib/api/client'
import {
    REFRESH_HTTP_TIMEOUT_MS,
    getLastRefreshFailure,
    tryRefreshToken,
} from '@/lib/api/client'
import { onSessionStarted } from '@/lib/auth-session-signal'
import { posthog } from '@/lib/posthog'
import { reportSseDegraded, reportSseRecovered } from '@/lib/sse-health-report'

/**
 * Self-healing EventSource wrapper for always-on screens (packing station,
 * office speaker, customer display). Plain EventSource dies permanently in
 * three real-world cases this wrapper recovers from:
 *
 * - `auth_expired`: the server kills every SSE stream once the access token
 *   from the ORIGINAL request expires (SSE headers are frozen at connect).
 *   Recovery: silent refresh, then reconnect so the new request carries a
 *   fresh cookie. On refresh failure keep retrying forever with capped
 *   backoff — unattended machines must self-recover once a valid session
 *   reappears. A login in any tab of this browser wakes recovery at once;
 *   a refresh the server REJECTED (401) is only re-probed slowly, since the
 *   session is gone until someone logs in again.
 * - Silent drops (NAT timeout, wifi switch, sleep/wake) where `onerror`
 *   never fires: a watchdog reconnects when nothing — including the server's
 *   25s `heartbeat` events — arrives within the window.
 * - Wake-up: `online` / tab-visible events skip any pending backoff.
 *
 * Concurrency invariant: at most ONE live connection, ever. Each connect()
 * bumps a generation counter; every handler belongs to one generation and
 * no-ops when it is stale. A stray connection would double-announce every
 * order on the station speaker, so this is load-bearing, not defensive.
 */

type EventHandler = (ev: MessageEvent) => void
type RefreshOutcome = 'ok' | RefreshFailureReason

export interface ResilientEventSourceOptions {
    url: string
    /** Named SSE events to forward. Do NOT register `auth_expired`/`heartbeat` — the wrapper owns those. */
    listeners: Record<string, EventHandler>
    onConnectedChange?: (connected: boolean) => void
    /** Override for tests. Default 60s = 2 missed server heartbeats + margin. */
    watchdogMs?: number
}

export interface ResilientEventSourceHandle {
    close: () => void
}

const DEFAULT_WATCHDOG_MS = 60_000
const ERROR_BACKOFF_CAP_MS = 60_000
// ~1 retry/min: a backend-restart window equals missed (unreplayable) order
// announcements, so always-on machines must probe frequently forever.
const REFRESH_RETRY_CAP_MS = 60_000
// A 401 from /auth/refresh is not an outage: the session is dead until a human
// logs in, and that login wakes recovery through the session signal. The slow
// probe only covers a login the signal missed (e.g. a tab on an older build).
const DEAD_SESSION_PROBE_MS = 10 * 60_000
// A refresh that outlives this is treated as failed — a hung request (TCP
// half-open after sleep, captive portal) must never wedge recovery. Derived
// from the HTTP-layer timeout so it can never fall below it: were this
// deadline the shorter one, every "retry" would await the same deduped
// in-flight request and recovery would stand still while appearing to retry.
const REFRESH_TIMEOUT_MS = REFRESH_HTTP_TIMEOUT_MS + 5_000
// Floor between a successful refresh and the reconnect. The server currently
// caps auth_expired at one per revalidation tick, but the client must not
// depend on that: without a floor, a server that validated on connect would
// turn recovery into a hot loop hammering /auth/refresh from an unattended
// kiosk.
const AUTH_RECONNECT_FLOOR_MS = 1_000
// Health reporting thresholds. The server-side checker owns THAT the station
// is offline (Telegram); these reports own WHY (Sentry). Report once the
// situation is clearly not a blip: ~5 minutes of failed refreshes, or five
// watchdog kills inside ten minutes (streams that open but stay silent).
const DEGRADED_AFTER_REFRESH_FAILURES = 5
const STALL_LOOP_WINDOW_MS = 10 * 60_000
const STALL_LOOP_THRESHOLD = 5
// Wait after the server evicted this stream (the account holds more live
// connections than the server allows). Minutes, not the usual seconds: an
// immediate reconnect evicts whichever device just took the slot, which evicts
// this one back, and the whole account spins at the reconnect interval.
const EVICTED_BACKOFF_MS = 5 * 60_000

// An eviction is invisible in the UI, so this event is the only way to see an
// account running over its device budget. Telemetry must never break the
// reconnect path it reports on.
function reportSseEvicted(): void {
    try {
        if (!posthog.__loaded) return
        posthog.capture('sse_evicted', {})
    } catch {
        // Analytics is best-effort; the reconnect below matters more.
    }
}

export function createResilientEventSource(
    options: ResilientEventSourceOptions,
): ResilientEventSourceHandle {
    const { url, listeners, onConnectedChange } = options
    const watchdogMs = options.watchdogMs ?? DEFAULT_WATCHDOG_MS

    // Every source ever opened and not yet closed, so close() can silence
    // strays even if an invariant is broken elsewhere.
    const sources = new Set<EventSource>()
    let gen = 0
    let es: EventSource | null = null
    let closed = false
    let connected = false
    let retryCount = 0
    let refreshRetryCount = 0
    // True from "cookie suspected dead" until a refresh succeeds. While set,
    // every reconnect path must go through recoverAuth, never straight to
    // connect() — a connect with a dead cookie just 401-loops.
    let authMode = false
    // Set while serving out the post-eviction wait. The wake-up shortcuts must
    // not skip it — reconnecting early restarts the eviction loop.
    let evictedCooldown = false
    let refreshInFlight: Promise<RefreshOutcome> | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null
    let watchdogFires: number[] = []

    const setConnected = (value: boolean) => {
        if (connected === value) return
        connected = value
        onConnectedChange?.(value)
    }

    const clearReconnectTimer = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer)
        reconnectTimer = null
    }

    const clearWatchdogTimer = () => {
        if (watchdogTimer) clearTimeout(watchdogTimer)
        watchdogTimer = null
    }

    const dropSource = (source: EventSource) => {
        source.close()
        sources.delete(source)
        if (es === source) {
            es = null
            setConnected(false)
        }
    }

    const errorBackoff = () =>
        Math.min(1000 * Math.pow(2, retryCount), ERROR_BACKOFF_CAP_MS)

    const scheduleReconnect = (delayMs: number) => {
        if (closed) return
        clearReconnectTimer()
        reconnectTimer = setTimeout(connect, delayMs)
    }

    // Never rejects, never hangs: rejection and timeout both mean "failed
    // attempt", so the retry loop always keeps moving. The failure reason is
    // this attempt's own — the client's sticky reason may predate a timeout.
    const attemptRefresh = () =>
        new Promise<RefreshOutcome>((resolve) => {
            const deadline = setTimeout(
                () => resolve('timeout'),
                REFRESH_TIMEOUT_MS,
            )
            tryRefreshToken().then(
                (ok) => {
                    clearTimeout(deadline)
                    resolve(ok ? 'ok' : (getLastRefreshFailure() ?? 'network'))
                },
                () => {
                    clearTimeout(deadline)
                    resolve('network')
                },
            )
        })

    // Serialized: concurrent triggers share one in-flight refresh, and only
    // the first resolver observes authMode still set and reconnects.
    const recoverAuth = async () => {
        authMode = true
        if (!refreshInFlight) {
            refreshInFlight = attemptRefresh().finally(() => {
                refreshInFlight = null
            })
        }
        const outcome = await refreshInFlight
        if (closed || !authMode) return
        if (outcome === 'ok') {
            authMode = false
            refreshRetryCount = 0
            scheduleReconnect(AUTH_RECONNECT_FLOOR_MS)
            return
        }
        refreshRetryCount++
        if (refreshRetryCount >= DEGRADED_AFTER_REFRESH_FAILURES) {
            reportSseDegraded({
                reason: outcome,
                retries: refreshRetryCount,
            })
        }
        const delay =
            outcome === 'unauthorized'
                ? DEAD_SESSION_PROBE_MS
                : Math.min(
                      1000 * Math.pow(2, refreshRetryCount),
                      REFRESH_RETRY_CAP_MS,
                  )
        clearReconnectTimer()
        reconnectTimer = setTimeout(() => void recoverAuth(), delay)
    }

    function connect() {
        if (closed) return
        evictedCooldown = false
        clearReconnectTimer()
        if (es) dropSource(es)
        gen++
        const myGen = gen

        let source: EventSource
        try {
            source = new EventSource(url, { withCredentials: true })
        } catch {
            retryCount++
            scheduleReconnect(errorBackoff())
            return
        }
        es = source
        sources.add(source)
        const stale = () => closed || myGen !== gen || es !== source

        // Any traffic proves liveness; silence past the window means the TCP
        // connection died without the browser ever firing onerror.
        const armWatchdog = () => {
            clearWatchdogTimer()
            watchdogTimer = setTimeout(() => {
                if (stale()) return
                const now = Date.now()
                watchdogFires = watchdogFires.filter(
                    (t) => now - t < STALL_LOOP_WINDOW_MS,
                )
                watchdogFires.push(now)
                if (watchdogFires.length >= STALL_LOOP_THRESHOLD) {
                    reportSseDegraded({
                        reason: 'stream_stall_loop',
                        retries: watchdogFires.length,
                    })
                }
                dropSource(source)
                connect()
            }, watchdogMs)
        }
        armWatchdog()

        source.onopen = () => {
            if (stale()) return
            retryCount = 0
            setConnected(true)
            armWatchdog()
        }

        source.onerror = () => {
            const wasStale = stale()
            dropSource(source)
            if (wasStale) return
            clearWatchdogTimer()
            retryCount++
            const delay = errorBackoff()
            // Persistent failures may be plain 401s from an expired cookie
            // (stream died silently during sleep — no auth_expired was ever
            // received). Route recovery through the session refresh; it only
            // reconnects once the refresh actually succeeds.
            if (retryCount >= 2) {
                clearReconnectTimer()
                reconnectTimer = setTimeout(() => void recoverAuth(), delay)
                return
            }
            scheduleReconnect(delay)
        }

        // Real traffic is the only trustworthy recovery signal — a stream
        // that OPENS but stays silent is exactly the stall being watched for.
        source.addEventListener('heartbeat', () => {
            if (stale()) return
            watchdogFires = []
            reportSseRecovered()
            armWatchdog()
        })

        // The frozen-cookie stream is unrecoverable server-side; only a new
        // request can carry the refreshed cookie.
        source.addEventListener('auth_expired', () => {
            const wasStale = stale()
            dropSource(source)
            if (wasStale) return
            clearWatchdogTimer()
            void recoverAuth()
        })

        // The server hands this stream's slot to a newer connection from the
        // same account. Keep retryCount: a device that keeps losing its slot
        // must escalate, not restart from one second.
        source.addEventListener('evicted', () => {
            const wasStale = stale()
            dropSource(source)
            if (wasStale) return
            clearWatchdogTimer()
            reportSseEvicted()
            evictedCooldown = true
            scheduleReconnect(EVICTED_BACKOFF_MS)
        })

        for (const [type, handler] of Object.entries(listeners)) {
            source.addEventListener(type, (event) => {
                if (stale()) return
                retryCount = 0
                watchdogFires = []
                reportSseRecovered()
                armWatchdog()
                handler(event)
            })
        }
    }

    const reconnectNowIfDown = () => {
        if (closed || connected || evictedCooldown) return
        clearReconnectTimer()
        if (authMode) {
            // Skip the capped wait and retry the refresh immediately.
            refreshRetryCount = 0
            void recoverAuth()
            return
        }
        if (!es) connect()
    }

    const onOnline = () => reconnectNowIfDown()
    const onVisible = () => {
        if (document.visibilityState === 'visible') reconnectNowIfDown()
    }

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    const stopSessionSignal = onSessionStarted(reconnectNowIfDown)

    connect()

    return {
        close: () => {
            closed = true
            window.removeEventListener('online', onOnline)
            document.removeEventListener('visibilitychange', onVisible)
            stopSessionSignal()
            clearReconnectTimer()
            clearWatchdogTimer()
            for (const source of sources) source.close()
            sources.clear()
            es = null
            setConnected(false)
        },
    }
}
